import { useMemo, useState } from 'react';
import { posts } from '@/mocks/home';

const categories = ['全部', ...Array.from(new Set(posts.map((p) => p.category)))];

export default function PostGrid() {
  const [active, setActive] = useState('全部');

  const visible = useMemo(
    () => (active === '全部' ? posts : posts.filter((p) => p.category === active)),
    [active],
  );
  const featured = visible.find((p) => p.pinned);
  const rest = visible.filter((p) => p.id !== featured?.id);

  return (
    <section id="posts" className="w-full bg-background-50 py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.24em] text-primary-600">
              <span className="w-6 h-px bg-primary-400" />
              FULL ARCHIVE
            </span>
            <h2 className="mt-4 font-heading font-black text-foreground-950 text-3xl md:text-4xl tracking-tight">
              灵灵黑料完整版 · 内容精选
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-foreground-600">
            按角度分区：深扒细节、原帖截图、未删内容各自成篇，避免把不同性质的材料混成一锅。
          </p>
        </div>

        <div className="mt-8 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-colors ${
                active === c
                  ? 'bg-foreground-950 text-background-50'
                  : 'bg-background-100 text-foreground-700 border border-background-200 hover:bg-background-200'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {featured && (
          <article className="mt-8 group grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-lg overflow-hidden border border-background-200/80 bg-background-100/60">
            <div className="lg:col-span-7 relative h-56 sm:h-72 lg:h-auto lg:min-h-[340px] w-full">
              <img
                src={featured.image}
                alt={featured.title}
                title={`${featured.title} 灵灵黑料 完整版`}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-label">
                <i className="ri-pushpin-2-fill text-xs" />
                置顶整理
              </span>
            </div>
            <div className="lg:col-span-5 p-6 md:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-3 text-xs font-label text-foreground-500">
                <span className="text-accent-600">{featured.category}</span>
                <span className="w-1 h-1 rounded-full bg-background-300" />
                <span>{featured.date}</span>
              </div>
              <h3 className="mt-4 font-heading font-bold text-foreground-950 text-xl md:text-2xl leading-snug">
                {featured.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">{featured.excerpt}</p>
              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-label text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-eye-line" />
                  {featured.reads}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <i className="ri-chat-1-line" />
                  {featured.comments}
                </span>
                <a
                  href="#comments"
                  className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors whitespace-nowrap"
                >
                  读全文
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </article>
        )}

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {rest.map((p, idx) => (
            <article
              key={p.id}
              style={{ animationDelay: `${idx * 40}ms` }}
              className="group flex flex-col rounded-lg overflow-hidden border border-background-200/80 bg-background-50 hover:border-primary-300 transition-colors ll-fade-up"
            >
              <div className="relative w-full h-44 md:h-48">
                <img
                  src={p.image}
                  alt={p.title}
                  title={`${p.title} 灵灵黑料 深扒细节`}
                  className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                />
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-background-50/90 backdrop-blur-sm text-xs font-label text-foreground-800">
                  {p.category}
                </span>
              </div>
              <div className="p-4 md:p-5 flex flex-col flex-1">
                <h3 className="font-heading font-bold text-foreground-950 text-base md:text-lg leading-snug line-clamp-2">
                  {p.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-foreground-600 line-clamp-3">
                  {p.excerpt}
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-[11px] font-label whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="mt-4 pt-3.5 border-t border-background-200/80 flex items-center justify-between text-xs font-label text-foreground-500">
                  <span>{p.date}</span>
                  <span className="inline-flex items-center gap-3">
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-eye-line" />
                      {p.reads}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <i className="ri-chat-1-line" />
                      {p.comments}
                    </span>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}